export type CopyResult = 'clipboard' | 'fallback' | 'failed';

/**
 * Copy text to the clipboard with tiered fallbacks. The Zoom desktop client's
 * embedded webview blocks the async `navigator.clipboard` API in our iframe,
 * so we fall back to the legacy `document.execCommand('copy')` path (which
 * still works there). If even that fails, the caller can select a DOM node
 * for the user to ⌘C manually.
 */
export async function copyText(text: string): Promise<CopyResult> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return 'clipboard';
    } catch {
      /* fall through */
    }
  }

  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.left = '0';
  ta.style.opacity = '0';
  ta.style.pointerEvents = 'none';
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);

  try {
    const ok = document.execCommand('copy');
    return ok ? 'fallback' : 'failed';
  } catch {
    return 'failed';
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Programmatically select the text inside an element so the user can hit ⌘C
 * to copy. Used as the last-resort UX when both clipboard paths fail.
 */
export function selectElement(el: HTMLElement): void {
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}
